import { Directive, DoCheck, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormatConfigService } from '@ctsolution/ct-framework';

/**
 * Input testuale per valori numerici che mostra SEMPRE i decimali di configurazione
 * (anche in digitazione e quando disabilitato), a differenza di <input type="number">
 * che perde gli zeri finali (es. 1000 invece di 1.000,00).
 *
 * - In sola visualizzazione (non focus) formatta con Intl.NumberFormat (virgola decimale,
 *   punto separatore migliaia), coerente con la pipe formatNumber.
 * - In digitazione (focus) mostra la virgola decimale senza separatore migliaia, per una
 *   modifica agevole; il modello resta un number.
 *
 * Uso: <input appDecimalFormat [(ngModel)]="qty">  oppure  appDecimalFormat="3" per forzare i decimali.
 */
@Directive({
  selector: 'input[appDecimalFormat]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DecimalFormatDirective), multi: true }
  ]
})
export class DecimalFormatDirective implements ControlValueAccessor, DoCheck {

  /** Decimali espliciti; se non impostato usa i decimali di configurazione (in base a decimalKind). */
  @Input('appDecimalFormat') digitsInput: number | string | null = null;

  /** Quali decimali di configurazione usare: 'number' (default) o 'price'. */
  @Input() decimalKind: 'number' | 'price' = 'number';

  /** Minimo opzionale: al blur i valori inferiori vengono riportati a questo valore. */
  @Input() appDecimalMin: number | null = null;

  private onChange: (value: number | null) => void = () => { };
  private onTouched: () => void = () => { };
  private focused = false;
  private currentValue: number | null = null;
  private lastRenderedDigits: number | null = null;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private formatConfig: FormatConfigService,
    private translate: TranslateService
  ) { }

  private get digits(): number {
    const explicit = typeof this.digitsInput === 'string' ? parseInt(this.digitsInput, 10) : this.digitsInput;
    if (explicit !== null && explicit !== undefined && !isNaN(explicit)) return explicit;
    return this.decimalKind === 'price'
      ? this.formatConfig.defaultPriceDigits
      : this.formatConfig.defaultNumberDigits;
  }

  /**
   * I decimali di configurazione vengono caricati in modo asincrono: quando cambiano
   * (o quando cambia il valore dall'esterno) riformatto la visualizzazione, purché
   * l'utente non stia digitando. Replica il comportamento della pipe formatNumber (pure:false).
   */
  ngDoCheck(): void {
    if (this.focused) return;
    if (this.lastRenderedDigits !== this.digits) {
      this.render();
    }
  }

  writeValue(value: any): void {
    this.currentValue = this.toNumber(value);
    this.render();
  }

  registerOnChange(fn: any): void { this.onChange = fn; }

  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('focus')
  onFocus(): void {
    this.focused = true;
    this.render();
    this.el.nativeElement.select();
  }

  @HostListener('input')
  onInput(): void {
    this.currentValue = this.parse(this.el.nativeElement.value);
    this.onChange(this.currentValue);
  }

  @HostListener('blur')
  onBlur(): void {
    this.focused = false;

    if (this.appDecimalMin !== null && this.currentValue !== null && this.currentValue < this.appDecimalMin) {
      this.currentValue = this.appDecimalMin;
      this.onChange(this.currentValue);
    }

    this.onTouched();
    this.render();
  }

  /** Aggiorna il valore mostrato nell'input in base allo stato (focus vs sola lettura). */
  private render(): void {
    this.lastRenderedDigits = this.digits;
    if (this.currentValue === null) {
      this.el.nativeElement.value = '';
      return;
    }
    this.el.nativeElement.value = this.focused
      ? this.toEdit(this.currentValue)
      : this.toDisplay(this.currentValue);
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return isNaN(n) ? null : n;
  }

  /** Formato di sola visualizzazione: coerente con la pipe formatNumber. */
  private toDisplay(num: number): string {
    return new Intl.NumberFormat(this.translate.currentLang || 'it-IT', {
      minimumFractionDigits: this.digits,
      maximumFractionDigits: this.digits
    }).format(num);
  }

  /** Formato editabile: virgola decimale, senza separatore delle migliaia. */
  private toEdit(num: number): string {
    return num.toFixed(this.digits).replace('.', ',');
  }

  /** Interpreta il testo digitato: rimuove i separatori migliaia e usa la virgola come decimale. */
  private parse(text: string): number | null {
    if (text == null) return null;
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const normalized = trimmed.replace(/[.\s]/g, '').replace(',', '.');
    const n = parseFloat(normalized);
    return isNaN(n) ? null : n;
  }
}
